import React, { useState } from "react"
import { Heart } from "lucide-react"
import useInfluencerFavourite from "../hooks/useInfluencerFavourite"
import { INFLUENCER_ACCENT_COLOR } from "../constants"

/**
 * Reusable heart icon button - a circular chip on InfluencerCard (top-right
 * corner, over the card's own click-to-open-profile handler) and a labeled
 * pill on the profile page's header. Owns its own optimistic save/unsave
 * state via useInfluencerFavourite so neither caller has to wire that up
 * itself.
 *
 * Hover/disabled states are plain inline-style toggles (useState + on
 * Enter/Leave), same convention InfluencerCard.jsx itself uses for its own
 * hover effect, rather than a `<style>` block - this renders once per card
 * in a grid of 21, and a scoped `<style>` tag would duplicate that many
 * times over for no benefit.
 */
const FavouriteButton = ({ platform, username, isFavourite: initialIsFavourite, size = "icon", style }) => {
  const { isFavourite, isSaving, toggle } = useInfluencerFavourite({
    platform, username, initialIsFavourite,
  })
  const [hovered, setHovered] = useState(false)

  const label = isFavourite ? "Remove from favourites" : "Save to favourites"
  const isLabeled = size === "labeled"

  const sharedProps = {
    type: "button",
    onClick: toggle,
    disabled: isSaving,
    title: label,
    "aria-pressed": isFavourite,
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
  }

  if (isLabeled) {
    return (
      <button
        {...sharedProps}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: "7px",
          padding: "9px 16px", borderRadius: "10px",
          border: `1px solid ${isFavourite ? INFLUENCER_ACCENT_COLOR : "rgba(255,255,255,0.12)"}`,
          background: isFavourite ? `${INFLUENCER_ACCENT_COLOR}18` : (hovered ? "rgba(255,255,255,0.09)" : "rgba(255,255,255,0.05)"),
          color: isFavourite ? INFLUENCER_ACCENT_COLOR : "#e8e8e8",
          fontWeight: 600, fontSize: "13px", fontFamily: "Rubik, sans-serif",
          whiteSpace: "nowrap", cursor: isSaving ? "default" : "pointer",
          opacity: isSaving ? 0.6 : 1,
          ...style,
        }}
      >
        <Heart size={14} color={isFavourite ? INFLUENCER_ACCENT_COLOR : "#e8e8e8"} fill={isFavourite ? INFLUENCER_ACCENT_COLOR : "none"} />
        {isFavourite ? "Saved" : "Save"}
      </button>
    )
  }

  return (
    <button
      {...sharedProps}
      style={{
        width: "30px", height: "30px", borderRadius: "50%", border: "none",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: isFavourite ? `${INFLUENCER_ACCENT_COLOR}cc` : "rgba(10,10,14,0.55)",
        backdropFilter: "blur(2px)", flexShrink: 0,
        cursor: isSaving ? "default" : "pointer",
        opacity: isSaving ? 0.6 : 1,
        transform: hovered && !isSaving ? "scale(1.08)" : "none",
        transition: "transform 0.12s ease, background 0.15s ease",
        ...style,
      }}
    >
      <Heart size={13} color={isFavourite ? "#fff" : "#e8e8e8"} fill={isFavourite ? "#fff" : "none"} />
    </button>
  )
}

export default FavouriteButton
